import { useTranslation } from "react-i18next";

import { asActivityLevel, asMarketRegime, type RobotNow } from "@/entities/robot";
import { formatSlot } from "@/shared/lib";
import { Panel } from "@/shared/ui";

/**
 * «Робот сейчас» (ТЗ §10).
 *
 * Состояние разыгрывается по паре «дата + час», как и само начисление: блок
 * меняется раз в час и стабилен внутри часа. Поэтому подпись «обновлено в
 * HH:MM» не врёт — это действительно момент, на который посчитано состояние.
 *
 * Время показано абсолютным, а не «2 минуты назад», как просит ТЗ: относительную
 * подпись пришлось бы пересчитывать на клиенте, а правило ТЗ №5 оставляет
 * фронту только форматирование. К тому же «2 минуты назад» в кешированном на
 * тридцать секунд ответе устаревает быстрее, чем читается.
 */
export function RobotNowBlock({ name, state }: { name: string; state: RobotNow }) {
  const { t } = useTranslation();

  const rows: { label: string; value: string }[] = [
    {
      label: t("robots.analytics.now.regime"),
      value: t(`robots.analytics.regime.${asMarketRegime(state.regime)}`),
    },
    {
      label: t("robots.analytics.now.activity"),
      value: t(`robots.analytics.activity.${asActivityLevel(state.activity)}`),
    },
    { label: t("robots.analytics.now.mainAssets"), value: state.main_assets.join(" · ") },
    { label: t("robots.analytics.now.openPositions"), value: String(state.open_positions) },
  ].filter((row) => row.value !== "");

  return (
    <Panel as="section" tone="cyan" radius="lg" className="flex flex-col gap-3 p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {t("robots.analytics.now.title", { name })}
      </h2>
      <dl className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 text-caption">
            <dt className="font-normal text-text-secondary">{row.label}</dt>
            <dd className="min-w-0 text-right font-semibold text-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-micro font-normal text-text-tertiary">
        {t("robots.analytics.now.updatedAt", {
          time: formatSlot(state.updated_at, { withTime: true }),
        })}
      </p>
    </Panel>
  );
}
